import React from 'react';
import { Thermometer, Gauge, ClipboardCheck, CheckCircle2, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useTranslation, Trans } from 'react-i18next';

const Testing = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-[#0A1F44] text-white py-12 sm:py-16 md:py-20">
        <div className="max-w-[95%] sm:max-w-[90%] md:max-w-6xl mx-auto px-4 sm:px-6 text-center md:text-left">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            {t('testing.header.title')}
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl leading-relaxed mx-auto md:mx-0">
            {t('testing.header.description')}
          </p>
        </div>
      </section>

      <div className="max-w-[95%] sm:max-w-[90%] md:max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 space-y-10 sm:space-y-12">
        {/* High Temperature Test */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Thermometer className="h-6 sm:h-7 w-6 sm:w-7 text-[#FF6A00]" />
              <CardTitle className="text-xl sm:text-2xl text-[#0A1F44]">
                {t('testing.ht.title')}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-sm sm:text-base">
            <p className="text-gray-700 leading-relaxed">
              <Trans i18nKey="testing.ht.description" components={{ strong: <strong /> }} />
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm border border-gray-200 rounded-lg">
                <thead className="bg-gray-100 text-[#0A1F44] font-semibold">
                  <tr>
                    <th className="px-3 sm:px-4 py-2 text-left">{t('testing.ht.table.temp')}</th>
                    <th className="px-3 sm:px-4 py-2 text-left">{t('testing.ht.table.duration')}</th>
                    <th className="px-3 sm:px-4 py-2 text-left">{t('testing.ht.table.result')}</th>
                  </tr>
                </thead>
                <tbody>
                  {t('testing.ht.table.rows', { returnObjects: true }).map((row, i) => (
                    <tr key={i} className="border-t border-gray-200">
                      <td className="px-3 sm:px-4 py-2">{row.t}</td>
                      <td className="px-3 sm:px-4 py-2">{row.d}</td>
                      <td className="px-3 sm:px-4 py-2">{row.r}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-gray-700 leading-relaxed">
              {t('testing.ht.conclusion')}
            </p>
          </CardContent>
        </Card>

        {/* HTHP Testing */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Gauge className="h-6 sm:h-7 w-6 sm:w-7 text-[#FF6A00]" />
              <CardTitle className="text-xl sm:text-2xl text-[#0A1F44]">
                {t('testing.hthp.title')}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-sm sm:text-base">
            <p className="text-gray-700 leading-relaxed">
              <Trans i18nKey="testing.hthp.description" components={{ strong: <strong /> }} />
            </p>

            <ul className="list-disc ml-5 sm:ml-6 text-gray-700 space-y-2">
              {t('testing.hthp.list', { returnObjects: true }).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>

            <p className="text-gray-700 leading-relaxed">
              <Trans i18nKey="testing.hthp.conclusion" components={{ strong: <strong /> }} />
            </p>
          </CardContent>
        </Card>

        {/* Routine Check */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <div className="flex items-center gap-3">
              <ClipboardCheck className="h-6 sm:h-7 w-6 sm:w-7 text-[#FF6A00]" />
              <CardTitle className="text-xl sm:text-2xl text-[#0A1F44]">
                {t('testing.inspection.title')}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-sm sm:text-base">
            <p className="text-gray-700 leading-relaxed">
              {t('testing.inspection.description')}
            </p>

            <ol className="list-decimal ml-5 sm:ml-6 space-y-2 text-gray-700">
              {t('testing.inspection.steps', { returnObjects: true }).map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>

            <p className="text-gray-700 italic">
              {t('testing.inspection.note')}
            </p>
          </CardContent>
        </Card>

        {/* Calibration */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 sm:h-7 w-6 sm:w-7 text-[#FF6A00]" />
              <CardTitle className="text-xl sm:text-2xl text-[#0A1F44]">
                {t('testing.calibration.title')}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-sm sm:text-base">
            <p className="text-gray-700 leading-relaxed">
              {t('testing.calibration.description')}
            </p>

            <ol className="list-decimal ml-5 sm:ml-6 space-y-2 text-gray-700">
              {t('testing.calibration.steps', { returnObjects: true }).map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>

            <p className="text-gray-700 italic">
              {t('testing.calibration.note')}
            </p>
          </CardContent>
        </Card>

        {/* Certifications */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <div className="flex items-center gap-3">
              <FileText className="h-6 sm:h-7 w-6 sm:w-7 text-[#FF6A00]" />
              <CardTitle className="text-xl sm:text-2xl text-[#0A1F44]">
                {t('testing.certifications.title')}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 text-sm sm:text-base">
            <ul className="list-disc ml-5 sm:ml-6 text-gray-700 space-y-1 sm:space-y-2">
              {t('testing.certifications.list', { returnObjects: true }).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
            <div className="pt-4">
              <a href="mailto:support@hh-enterprise.net">
                <Button className="bg-[#FF6A00] hover:bg-[#E55F00] text-white text-sm sm:text-base">
                  {t('testing.certifications.button')}
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Testing;
